import { OwnedNft } from 'alchemy-sdk';
import camelCase from 'lodash/camelCase';
import { Address } from 'viem';

import { EVMNft, EVMNftMetadata } from '@repo/types-client';

import { getEvmNFTCollectionByAddress } from './get-evm-nft-collection-by-address';

/**
 * Input interface for converting an Alchemy NFT to our internal EVM NFT format
 * @interface ConvertAlchemyNftToEVMNftInput
 * @property {OwnedNft} alchemyNft - The NFT data from Alchemy API
 * @property {string} owner - The wallet address that owns this NFT
 */
interface ConvertAlchemyNftToEVMNftInput {
  alchemyNft: OwnedNft;
  owner: string;
}

/**
 * Converts an Alchemy NFT object to our internal EVM NFT format
 * @param {ConvertAlchemyNftToEVMNftInput} params - The input parameters
 * @param {OwnedNft} params.alchemyNft - The NFT data from Alchemy API
 * @param {string} params.owner - The wallet address that owns this NFT
 * @returns {EVMNft} The converted NFT in our internal format
 */
export const convertAlchemyNftToEVMNft = ({
  alchemyNft,
  owner,
}: ConvertAlchemyNftToEVMNftInput): EVMNft => {
  // Convert NFT attributes to a structured object format
  const structuredAttributes =
    alchemyNft.raw.metadata?.attributes?.reduce(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (acc: any, attribute: Record<string, any>) => {
        acc[camelCase(attribute.trait_type)] = attribute.value;
        return acc;
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      {} as Record<string, any>,
    ) || {};

  // Extract raw metadata for easier access
  const alchemyMetadata = alchemyNft.raw.metadata;

  // Construct standardized metadata object
  const metadata: EVMNftMetadata = {
    name: alchemyMetadata.name,
    image: alchemyMetadata.image,
    description: alchemyMetadata.description,
    animation_url: alchemyMetadata.animation_url,
    // Convert attributes to our standard format
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    attributes: alchemyMetadata.attributes?.map((attribute: any) => ({
      value: attribute.value.toString(),
      trait_type: attribute.trait_type,
      max_value: attribute.max_value,
    })),
  };

  // Get collection information for this NFT
  const nftCollection = getEvmNFTCollectionByAddress(
    alchemyNft.contract.address as Address,
  );

  // Construct the final EVMNft object
  const evmNFT: EVMNft = {
    tokenId: alchemyNft.tokenId,
    indexId: alchemyNft.tokenId,
    name: alchemyNft.name ?? '',
    description: alchemyNft.description ?? '',
    tokenType: nftCollection.tokenType,
    tokenUri: alchemyNft.tokenUri ?? '',
    image: {
      cachedUrl: alchemyNft.image.cachedUrl,
      thumbnailUrl: alchemyNft.image.thumbnailUrl,
      pngUrl: alchemyNft.image.pngUrl,
      contentType: alchemyNft.image.contentType,
      size: alchemyNft.image.size,
      originalUrl: alchemyNft.image.originalUrl ?? '',
    },
    metadata,
    contract: {
      address: nftCollection.getAddress(),
      name: nftCollection.contractName,
    },
    balance: alchemyNft.balance,
    acquiredAt: {
      blockTimestamp: alchemyNft.acquiredAt?.blockTimestamp,
      blockNumber: alchemyNft.acquiredAt?.blockNumber,
    },
    structuredAttributes,
    owner,
  };

  return evmNFT;
};
